import { keyBy } from 'lodash';
import { DataObject, ObjectPropertyValuesWithRoleType } from '@sageworks/dynamic-forms';
import { AutoFactory, DeleteDetails, ObjectPropertyValues, OnlinePortalObjectsByPropertyValuesApi } from '@sageworks/jpi';
import { defineActions } from 'direct-vuex';
import { actionContext } from '../store-helper';
import { LoadDataObjectParams, RemoveDataObjectParams, SaveDataObjectParams } from './persist-action-params';
import { FormDataModelConverter } from '../../../utils/form-data-model-converter';
import { DataObjectFetchHelper } from '../helpers';

const isEntityDataType = (dataObjectType: string): boolean => {
	const entityDataTypes = [
		DataObject.TypeEnum.Person,
		DataObject.TypeEnum.Business,
		DataObject.TypeEnum.NonProfit,
		DataObject.TypeEnum.Farm,
		DataObject.TypeEnum.Contact
	];
	return entityDataTypes.includes(dataObjectType as DataObject.TypeEnum);
};

export default defineActions({
	async saveDataObject(context, { dataObjectType, dataObject }: SaveDataObjectParams): Promise<any> {
		const { commit, rootDispatch, rootGetters } = actionContext(context);
		const dataObjectService = AutoFactory.get(OnlinePortalObjectsByPropertyValuesApi);
		const { id, dataFieldValues } = dataObject;

		if (!rootGetters.User.isLender && isEntityDataType(dataObjectType)) {
			await rootDispatch.BorrowerAccessRestrictions.fetchCurrentUserWritableEntities();
			if (id && id > 0 && !rootGetters.BorrowerAccessRestrictions.canUserWriteToEntity(id)) {
				return dataObject;
			}
		}

		let response: ObjectPropertyValues;
		if (id && id > 0) {
			if ((dataObject as ObjectPropertyValuesWithRoleType).canEditRoleEntity === false) {
				response = dataObject;
			} else {
				response = await dataObjectService.update(dataObjectType, id, { dataFieldValues });
			}
		} else {
			response = await dataObjectService.createSingle(dataObjectType, { dataFieldValues });
		}

		commit.UPSERT_DATA_OBJECT({ type: dataObjectType, dataObject: FormDataModelConverter.convertObjectPropertyValuesToDataObject(response) });

		return response;
	},
	async removeDataObject(context, { dataObjectType, id }: RemoveDataObjectParams): Promise<void> {
		const { commit, getters } = actionContext(context);
		const dataObjectService = AutoFactory.get(OnlinePortalObjectsByPropertyValuesApi);

		if (id && id > 0) {
			const deleteDetails: DeleteDetails = { loanID: getters.currentMetadata.proposedLoanId };
			await dataObjectService._delete(dataObjectType, id, deleteDetails);
			commit.REMOVE_DATA_OBJECT({ type: dataObjectType, id });
		}
	},
	async loadDataObjects(context, { ids, type, dataFieldMetadata }: LoadDataObjectParams): Promise<DataObject[]> {
		const {
			state: { renderData = {} },
			commit
		} = actionContext(context);

		let dataObjects = (renderData?.formData?.dataObjects ?? {})[type] ?? [];
		const currentDataObjects = keyBy(dataObjects, 'rowId');

		// Only fetch objects that are not already loaded
		const filteredIds = ids.filter(id => !currentDataObjects[id]);
		const retrievedDataObjects = await DataObjectFetchHelper.loadDataObjects(filteredIds, type, dataFieldMetadata);

		retrievedDataObjects.forEach(dataObject => commit.UPSERT_DATA_OBJECT({ type, dataObject }));

		dataObjects = dataObjects.concat(retrievedDataObjects);

		return dataObjects;
	}
});
